import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus,
  BookOpen,
  Radio,
  FileEdit,
  Users as UsersIcon,
  Pencil,
  Copy,
  X,
  Circle,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const TRACKS = [
  { value: 'cyber',  label: 'Cyber' },
  { value: 'aiml',   label: 'AI/ML' },
  { value: 'data',   label: 'Data' },
  { value: 'career', label: 'Career' },
];

const LEVELS = [
  { value: 'foundation',   label: 'Foundation' },
  { value: 'practitioner', label: 'Practitioner' },
  { value: 'advanced',     label: 'Advanced' },
];

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const formatGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);

// ─── Track badge ────────────────────────────────────────────────────
function TrackBadge({ track }) {
  const label = TRACKS.find((t) => t.value === track)?.label || track;
  return (
    <span
      className="inline-flex items-center font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{
        background: `var(--track-${track}-bg)`,
        color: `var(--track-${track}-fg)`,
        border: `1px solid var(--track-${track}-fg)`,
        borderColor: `var(--track-${track}-fg)`,
        borderOpacity: 0.3,
      }}
    >
      {label}
    </span>
  );
}

// ─── Status badge (green pulsing / amber static) ────────────────────
function StatusBadge({ status }) {
  const isLive = status === 'live';
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{
        background: isLive ? 'var(--ok-10)' : 'var(--warn-10)',
        color: isLive ? 'var(--ok)' : 'var(--warn)',
      }}
    >
      <Circle
        size={8}
        fill={isLive ? 'var(--ok)' : 'var(--warn)'}
        style={{
          color: isLive ? 'var(--ok)' : 'var(--warn)',
          animation: isLive ? 'pulseDot 2s ease-in-out infinite' : 'none',
        }}
      />
      {isLive ? 'Live' : 'Draft'}
    </span>
  );
}

// ─── Summary stat card ──────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="p-5 rounded-lg" style={CARD_STYLE}>
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {label}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{ width: 28, height: 28, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: 'var(--white)' }}>
        {new Intl.NumberFormat('en-GB').format(value)}
      </p>
    </div>
  );
}

// ─── Course create/edit modal ───────────────────────────────────────
function CourseModal({ mode, initial, onCancel, onSave, saving }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(initial || {
    name: '',
    track: 'cyber',
    duration_weeks: 8,
    default_level: 'foundation',
    description: '',
    status: 'draft',
    prices: { foundation: 0, practitioner: 0, advanced: 0 },
  });
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onCancel]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrice = (lvl, v) => setForm((f) => ({ ...f, prices: { ...f.prices, [lvl]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await onSave(form);
    } catch (ex) {
      setErr(ex.message || 'Save failed');
    }
  };

  return (
    <div
      data-testid="admin-course-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,5,10,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg p-6"
        style={CARD_STYLE}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
              {isEdit ? 'edit · course' : 'new · course'}
            </p>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--white)' }}>
              {isEdit ? 'Edit Course' : 'Add Course'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded"
            style={{ color: 'var(--ts)' }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {err && (
          <div
            data-testid="admin-course-modal-error"
            className="flex items-center gap-2 p-2 rounded mb-3"
            style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}
          >
            <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{err}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Course Name</label>
            <Input
              data-testid="admin-course-modal-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Cloud Security Essentials"
              className="font-body text-sm h-10 rounded-[3px]"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Track</label>
              <select
                data-testid="admin-course-modal-track"
                value={form.track}
                onChange={(e) => set('track', e.target.value)}
                className="w-full font-body text-sm h-10 rounded-[3px] px-2"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              >
                {TRACKS.map((t) => <option key={t.value} value={t.value} style={{ background: 'var(--l1)' }}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Duration (weeks)</label>
              <Input
                data-testid="admin-course-modal-duration"
                type="number"
                min={1}
                max={52}
                value={form.duration_weeks}
                onChange={(e) => set('duration_weeks', Number(e.target.value))}
                className="font-mono text-sm h-10 rounded-[3px]"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
                required
              />
            </div>
          </div>

          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Level</label>
            <select
              data-testid="admin-course-modal-level"
              value={form.default_level}
              onChange={(e) => set('default_level', e.target.value)}
              className="w-full font-body text-sm h-10 rounded-[3px] px-2"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
            >
              {LEVELS.map((l) => <option key={l.value} value={l.value} style={{ background: 'var(--l1)' }}>{l.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {LEVELS.map((lvl) => (
              <div key={lvl.value}>
                <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>
                  {lvl.label} £
                </label>
                <Input
                  data-testid={`admin-course-modal-price-${lvl.value}`}
                  type="number"
                  min={0}
                  step={1}
                  value={form.prices[lvl.value]}
                  onChange={(e) => setPrice(lvl.value, Number(e.target.value))}
                  className="font-mono text-sm h-10 rounded-[3px]"
                  style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
                  required
                />
              </div>
            ))}
          </div>

          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Short Description</label>
            <textarea
              data-testid="admin-course-modal-description"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full font-body text-sm rounded-[3px] p-2 resize-none"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              placeholder="Short marketing description (max 500 chars)"
            />
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-3">
              <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>Status</span>
              <button
                type="button"
                data-testid="admin-course-modal-status"
                onClick={() => set('status', form.status === 'live' ? 'draft' : 'live')}
                className="relative w-11 h-6 rounded-full transition-colors"
                style={{ background: form.status === 'live' ? 'var(--a)' : 'var(--b2)' }}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-transform"
                  style={{
                    left: 2,
                    background: 'var(--white)',
                    transform: form.status === 'live' ? 'translateX(20px)' : 'translateX(0)',
                  }}
                />
              </button>
              <StatusBadge status={form.status} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="font-body text-xs px-3 py-2 rounded-[3px]"
            style={{ color: 'var(--tm)', border: '1px solid var(--b1)' }}
          >
            Cancel
          </button>
          <Button
            type="submit"
            data-testid="admin-course-modal-save"
            disabled={saving}
            className="btn-shimmer font-body text-xs px-4 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Course'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────
export default function CourseManagerSection() {
  const { adminFetch } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', course?}
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/courses`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const courses = data?.courses || [];
  const summary = data?.summary || { total_courses: 0, live_courses: 0, draft_courses: 0, total_enrolments: 0 };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const isEdit = modal.mode === 'edit';
      const url = isEdit
        ? `${BACKEND_URL}/api/admin/courses/${modal.course.id}`
        : `${BACKEND_URL}/api/admin/courses`;
      const res = await adminFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed: ${res.status}`);
      }
      setModal(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (course) => {
    setDuplicatingId(course.id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/courses/${course.id}/duplicate`, { method: 'POST' });
      if (!res.ok) throw new Error(`Duplicate failed: ${res.status}`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleToggleStatus = async (course) => {
    setTogglingId(course.id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/courses/${course.id}/toggle-status`, { method: 'POST' });
      if (!res.ok) throw new Error(`Toggle failed: ${res.status}`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8 rounded-lg" style={CARD_STYLE}>
        <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading courses…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-course-manager-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · course-manager
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
            Course Manager
          </h2>
        </div>
        <Button
          data-testid="admin-course-add"
          onClick={() => setModal({ mode: 'create' })}
          className="btn-shimmer font-body text-sm h-10 px-4 rounded-[3px]"
          style={{ background: 'var(--a)', color: 'var(--white)' }}
        >
          <Plus size={14} className="mr-1.5" />
          Add Course
        </Button>
      </div>

      {error && (
        <div
          className="p-3 rounded"
          style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}
        >
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-course-summary">
        <StatCard label="Total Courses"     value={summary.total_courses}    icon={BookOpen}  testId="admin-course-stat-total" />
        <StatCard label="Live Courses"      value={summary.live_courses}     icon={Radio}     testId="admin-course-stat-live" />
        <StatCard label="Draft Courses"     value={summary.draft_courses}    icon={FileEdit}  testId="admin-course-stat-draft" />
        <StatCard label="Total Enrolments"  value={summary.total_enrolments} icon={UsersIcon} testId="admin-course-stat-enrolments" />
      </div>

      {/* Courses table */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-course-table">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--b1)', background: 'var(--l2)' }}>
              <Th>Course Name</Th>
              <Th>Track</Th>
              <Th align="right">Duration</Th>
              <Th align="right">Foundation</Th>
              <Th align="right">Practitioner</Th>
              <Th align="right">Advanced</Th>
              <Th align="right">Enrolments</Th>
              <Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c, idx) => (
              <tr
                key={c.id}
                data-testid={`admin-course-row-${c.id}`}
                style={{ borderBottom: idx === courses.length - 1 ? 'none' : '1px solid var(--b1)' }}
              >
                <Td>
                  <span className="font-body text-sm" style={{ color: 'var(--white)' }}>{c.name}</span>
                </Td>
                <Td><TrackBadge track={c.track} /></Td>
                <Td align="right">
                  <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>{c.duration_weeks}w</span>
                </Td>
                <Td align="right"><Price value={c.prices.foundation} /></Td>
                <Td align="right"><Price value={c.prices.practitioner} /></Td>
                <Td align="right"><Price value={c.prices.advanced} /></Td>
                <Td align="right">
                  <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>
                    {c.enrolments}
                  </span>
                </Td>
                <Td>
                  <button
                    data-testid={`admin-course-status-toggle-${c.id}`}
                    onClick={() => handleToggleStatus(c)}
                    disabled={togglingId === c.id}
                    title="Toggle status (Live ↔ Draft)"
                    className="hover:opacity-80 transition-opacity"
                    style={{ opacity: togglingId === c.id ? 0.5 : 1 }}
                  >
                    <StatusBadge status={c.status} />
                  </button>
                </Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <IconButton
                      testId={`admin-course-edit-${c.id}`}
                      title="Edit"
                      onClick={() => setModal({ mode: 'edit', course: c })}
                    >
                      <Pencil size={12} />
                    </IconButton>
                    <IconButton
                      testId={`admin-course-duplicate-${c.id}`}
                      title="Duplicate"
                      onClick={() => handleDuplicate(c)}
                      disabled={duplicatingId === c.id}
                    >
                      <Copy size={12} />
                    </IconButton>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <CourseModal
          mode={modal.mode}
          initial={modal.course}
          onCancel={() => setModal(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

// ─── Small table helpers ────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3"
      style={{ color: 'var(--ts)', textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td className="px-4 py-3" style={{ textAlign: align, verticalAlign: 'middle' }}>{children}</td>;
}
function Price({ value }) {
  return (
    <span className="font-mono text-xs" style={{ color: value === 0 ? 'var(--te)' : 'var(--tm)' }}>
      {formatGBP(value)}
    </span>
  );
}
function IconButton({ children, onClick, title, testId, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center rounded transition-colors"
      style={{
        width: 28,
        height: 28,
        color: 'var(--tm)',
        border: '1px solid var(--b1)',
        background: 'transparent',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
